import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, apiError } from "@/lib/api";
import { rupee } from "@/lib/format";
import MediaUpload from "@/components/MediaUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function Row({ l, v, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-heading font-extrabold text-base" : ""}`}>
      <span className={bold ? "" : "text-muted-foreground"}>{l}</span>
      <span className={bold ? "text-primary" : ""}>{v}</span>
    </div>
  );
}

export default function PaymentModal({ payment, open, onClose, onDone }) {
  const [qr, setQr] = useState(null);
  const [screenshot, setScreenshot] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setScreenshot(""); api.get("/payment-qr").then(({ data }) => setQr(data)); }
  }, [open]);

  const submit = async () => {
    if (!screenshot) { toast.error("Upload your payment screenshot"); return; }
    setBusy(true);
    try {
      await api.post(`/payments/${payment.id}/screenshot`, { screenshot_url: screenshot });
      toast.success("Screenshot uploaded. Awaiting admin approval.");
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!payment) return null;
  const b = payment.breakdown || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto ct-scroll" data-testid="payment-modal">
        <DialogHeader><DialogTitle className="font-heading">Complete Payment</DialogTitle></DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          {/* LEFT: amount, breakdown, QR */}
          <div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground font-semibold">Amount payable (incl. GST)</div>
              <div className="font-heading text-3xl font-extrabold text-primary" data-testid="payment-amount">{rupee(payment.amount)}</div>
              <div className="text-xs text-muted-foreground font-mono mt-1">{payment.invoice_no}</div>
            </div>

            <div className="mt-3 bg-secondary rounded-xl p-3 text-sm font-medium space-y-1" data-testid="payment-breakdown">
              {b.base != null && <Row l={b.products ? `${b.products} Products plan` : "Package cost"} v={rupee(b.base)} />}
              {b.slots_amount > 0 && <Row l={`Additional slots${b.additional_slots ? ` (${b.additional_slots})` : ""}`} v={rupee(b.slots_amount)} />}
              <Row l="Subtotal" v={rupee(b.subtotal ?? payment.amount)} />
              <Row l={`GST (${b.gst_rate ?? 18}%)`} v={rupee(b.gst ?? 0)} />
              <div className="border-t pt-1"><Row l="Grand Total" v={rupee(b.total ?? payment.amount)} bold /></div>
            </div>

            {qr?.qr && <img src={fileUrl(qr.qr)} alt="Payment QR" className="mx-auto mt-4 w-52 h-52 object-contain rounded-xl border bg-white p-3" data-testid="payment-qr-image" />}
            {qr?.upi && <div className="mt-2 text-sm font-bold text-center">UPI: {qr.upi}</div>}
            <p className="text-xs text-muted-foreground mt-1 text-center">Scan the QR to pay, then upload a screenshot.</p>

            <div data-testid="payment-instruction" className="mt-4 bg-amber-50 border border-amber-300 rounded-xl p-3">
              <p className="text-sm font-bold text-amber-900">Please send ₹1, get it approved, then send the remaining {rupee(Math.max(0, payment.amount - 1))}.</p>
              <p className="text-xs text-amber-800 mt-1">Pay ₹1 first and upload that screenshot. Once admin approves, pay the balance to the same QR.</p>
            </div>
          </div>

          {/* RIGHT: upload, submit, disclaimers */}
          <div className="flex flex-col">
            <MediaUpload testId="upload-payment-screenshot" label="Payment Screenshot" kind="image" value={screenshot} onChange={setScreenshot} />

            <Button data-testid="submit-payment-screenshot" onClick={submit} disabled={busy} className="w-full font-bold mt-4 h-11">
              {busy ? "Submitting…" : "Submit for Admin Approval"}
            </Button>

            <div data-testid="launch-disclaimer" className="mt-4 bg-amber-50 border-2 border-amber-300 rounded-xl p-3">
              <p className="text-sm font-extrabold text-amber-900">Please note before you pay</p>
              <p className="text-xs font-semibold text-amber-800 mt-1 leading-relaxed">
                Cleartile.in is a newly launched marketplace and we are actively onboarding sellers and buyers city by city. As the platform is new, it may take some time for buyer enquiries to build up in your city. Your subscription is valid for exactly 1 year from the date of activation, and payments are strictly for listing your stock on the platform.
              </p>
            </div>

            <div data-testid="payment-disclaimer" className="mt-4 bg-rose-50 border-2 border-rose-300 rounded-xl p-3">
              <p className="text-sm font-extrabold text-rose-900 uppercase tracking-wide">Disclaimer</p>
              <p className="text-xs font-bold text-rose-800 mt-1 leading-relaxed">
                Cleartile.in is only a referral platform connecting possible buyers to sellers. We do NOT guarantee any sales, orders or business outcomes. Subscription payments are for platform listing only.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
